// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — dynamic category descriptors
// ═══════════════════════════════════════════════════════════════════════════
//
// Category: "dynamic" — dynamic block authoring + customProperties CLI.
//
//   1. INSERT customProperties CLI (DYNLIST / DYNSET / DYNRESET).
//      Dispatched as `dynamic/list` / `dynamic/set` / `dynamic/reset`
//      from dynamicBlockCommandSchema (editor.ts:1415-1433, split out of
//      editorCommandSchema to stay below the ~109-variant TypeScript
//      inference threshold). Routed by typed name in App.tsx:1404-1573.
//      status: "implemented".
//
//   2. Block authoring wrappers (BPARAMETER / BACTION / BAUTHORPALETTE /
//      BVSTATE / BCPARAMETER / BLOOKUPTABLE / BTABLE / BGRIPSET).
//      Typed-name wrappers around the Block Authoring Palette. Each one
//      switches the palette to a tab and the actual mutations dispatch
//      from the palette editors as the user fills in details. Most of
//      them now own the corresponding authoring literals so that the
//      validator's coverage check passes:
//        BPARAMETER → geometry/{add,update,remove}-dynamic-parameter
//        BACTION    → geometry/{add,update,remove}-dynamic-action
//                   + geometry/create-chain-group
//                   + geometry/{add-action-to,remove-action-from,reorder}-chain-group
//                     (chain groups are coordination of actions; semantically
//                      they belong to the BACTION descriptor — we therefore
//                      do NOT mint a separate BCHAINGROUP command)
//        BVSTATE    → geometry/{add,rename,delete,reorder}-visibility-state(s)
//                   + geometry/set-default-visibility-state
//                   + geometry/toggle-visibility-state-entity
//      Exceptions kept stub-status: BAUTHORPALETTE (pure visibility
//      toggle), BCPARAMETER (palette tab switch only — actual binding
//      goes through GCCONSTRAINT), BGRIPSET (Tier 2 grip-management
//      stub). BLOOKUPTABLE and BTABLE were already implemented earlier.
//
// Sources of truth: editor.ts:752-932, :1415-1433; App.tsx:1404-1909;
// messages.ts:2099-2103, :2247-2264, :2371-2372;
// dynamicAuthoringReducerHelpers.ts (handle{AddDynamicParameter,
// AddDynamicAction, AddVisibilityState, CreateChainGroup, …}).
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const REDUCER_GEOMETRY = "apps/web/src/app/state/geometryReducer.ts" as const;
const REDUCER_DYNAMIC_LOOKUP =
  "apps/web/src/app/state/dynamicLookupReducerHelpers.ts" as const;
const REDUCER_DYNAMIC_AUTHORING =
  "apps/web/src/app/state/dynamicAuthoringReducerHelpers.ts" as const;

// ╔══ DYN CLI surface (DYNLIST / DYNSET / DYNRESET) ══════════════════════╗

const DYNLIST: CommandDescriptor = {
  id: "DYNLIST",
  category: "dynamic",
  status: "implemented",
  // Read-only query (lists dynamic properties of an insert) — not AI-callable.
  aiCallable: false,
  aliases: { en: ["DYNLIST", "DL"], ru: ["ДИНСПИСОК", "ДИНСПИС"] },
  commandTypeLiterals: ["dynamic/list"],
  parameters: [
    {
      name: "entityId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор INSERT (Block reference), для которого выводится список customProperties. Резолвится из текущего выбора пользователя.",
        en: "INSERT (Block reference) entity id whose customProperties are listed. Resolved from the current selection.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Выводит customProperties выбранного INSERT с типами и допустимыми значениями.",
      long: "Команда ДИНСПИСОК (DYNLIST) выводит в командную строку плоский набор customProperties выбранного блока INSERT — пары имя/значение, заполненные DXF-bridge'ом из default'ов параметров мастер-блока. К каждому ключу выводится выведенный тип (string/number/boolean), а если в block.dynamicMetadata.parameters найдена типизированная информация — также допустимые значения для visibility/lookup и default. Перед запуском нужно выбрать ровно один Block (INSERT). Команда не модифицирует геометрию — это только просмотр состояния параметров.",
      examples: ["ДИНСПИСОК", "DL"],
      seeAlso: ["DYNSET", "DYNRESET"],
    },
    en: {
      short:
        "Prints the selected INSERT's customProperties with inferred types and allowed values.",
      long: "The DYNLIST command dumps the selected INSERT's customProperties bag — name/value pairs the DXF bridge populated from the master block's authored parameter defaults. Each key shows its inferred type (string/number/boolean), and when block.dynamicMetadata.parameters contains typed metadata DYNLIST also lists choices for visibility/lookup parameters plus the default value. The user must have exactly one Block (INSERT) selected; otherwise the command emits a warning to the feed. DYNLIST is read-only — no geometry mutation.",
      examples: ["DYNLIST", "DL"],
      seeAlso: ["DYNSET", "DYNRESET"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.dynlist.label",
    labelKeyEn: "command.dynlist.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "dynamic/list", file: REDUCER_GEOMETRY },
  ],
};

const DYNSET: CommandDescriptor = {
  id: "DYNSET",
  category: "dynamic",
  status: "implemented",
  // Sets a dynamic-block property value on an INSTANCE — backed by the AI
  // UPDATE_DYNAMIC_PARAMETER / SET_VISIBILITY_STATE / LOOKUP_SELECT
  // safe-contour actions. AI-callable.
  aiCallable: true,
  aliases: { en: ["DYNSET", "DS"], ru: ["ДИНУСТАН", "ДИНЗАД"] },
  commandTypeLiterals: ["dynamic/set"],
  parameters: [
    {
      name: "entityId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор INSERT, у которого изменяется customProperty. Резолвится из текущего выбора.",
        en: "INSERT id whose customProperty is updated. Resolved from the current selection.",
      },
    },
    {
      name: "paramName",
      type: "string",
      required: true,
      description: {
        ru: "Имя параметра customProperty (строка после имени команды). Сохраняет регистр исходной строки.",
        en: "customProperty parameter name (the first token after the command name). Original casing is preserved.",
      },
    },
    {
      name: "rawValue",
      type: "string",
      required: true,
      description: {
        ru: "Значение в виде строки (всё после имени параметра). Парсится против текущего типа: number / boolean / string. Несовпадение типов даёт предупреждение и no-op.",
        en: "Value as a raw string (everything after the parameter name). Parsed against the current value's type: number / boolean / string. Type mismatch yields a warning and a no-op.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Изменяет одно поле customProperties выбранного INSERT (с проверкой типа).",
      long: "Команда ДИНУСТАН (DYNSET) обновляет одно значение в customProperties выбранного блока INSERT. Синтаксис: `DYNSET <имя_параметра> <значение>`. Значение парсится против текущего типа — числа конвертируются через parseFloat, boolean принимает true/false/yes/no/1/0, строки сохраняются как есть. Для visibility / lookup параметров значение должно совпадать с одним из choices (метаданные читаются из block.dynamicMetadata.parameters). При неизвестном имени или несовпадении типа команда выводит предупреждение в командную строку и не изменяет состояние. Перед запуском должен быть выбран один Block.",
      examples: [
        "DYNSET DOOR_WIDTH 1200",
        "ДИНУСТАН ВИДИМОСТЬ Закрыто",
        "DS angle 45",
      ],
      seeAlso: ["DYNLIST", "DYNRESET"],
    },
    en: {
      short:
        "Updates one customProperty field of the selected INSERT (with type checking).",
      long: "The DYNSET command updates a single value in the selected INSERT's customProperties bag. Syntax: `DYNSET <param_name> <value>`. The value is parsed against the current type — numbers via parseFloat, booleans accept true/false/yes/no/1/0, strings stored verbatim. For visibility / lookup parameters the value must match one of the choices (metadata read from block.dynamicMetadata.parameters). Unknown name or type mismatch emits a warning to the feed and leaves state unchanged. Requires exactly one Block selected before invocation.",
      examples: [
        "DYNSET DOOR_WIDTH 1200",
        "DYNSET visibility Closed",
        "DS angle 45",
      ],
      seeAlso: ["DYNLIST", "DYNRESET"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.dynset.label",
    labelKeyEn: "command.dynset.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "dynamic/set", file: REDUCER_GEOMETRY },
  ],
};

const DYNRESET: CommandDescriptor = {
  id: "DYNRESET",
  category: "dynamic",
  status: "implemented",
  // DEFAULT-FALSE (flagged for review): resets dynamic params to defaults; no
  // dedicated AI safe-contour action exists. Fail-closed until decided.
  aiCallable: false,
  aliases: { en: ["DYNRESET", "DR"], ru: ["ДИНСБРОС"] },
  commandTypeLiterals: ["dynamic/reset"],
  parameters: [
    {
      name: "entityId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор INSERT, у которого customProperties будут сброшены к authored-значениям мастер-блока.",
        en: "INSERT id whose customProperties are reset to the master block's authored defaults.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Сбрасывает customProperties выбранного INSERT к значениям по умолчанию мастер-блока.",
      long: "Команда ДИНСБРОС (DYNRESET) пересеивает customProperties выбранного INSERT, вызывая attachInsertCustomProperties для одной сущности. Все ранее установленные через DYNSET значения сбрасываются к authored-default'ам, прописанным в parameters мастер-блока (block.dynamicMetadata.parameters[*].defaultValue). Команда полезна когда пользователь экспериментировал с DYNSET и хочет вернуть исходное состояние. Геометрия не пересчитывается автоматически — это только storage-only операция.",
      examples: ["DYNRESET", "DR", "ДИНСБРОС"],
      seeAlso: ["DYNLIST", "DYNSET"],
    },
    en: {
      short:
        "Resets the selected INSERT's customProperties to the master block's authored defaults.",
      long: "The DYNRESET command re-seeds customProperties on the selected INSERT by invoking attachInsertCustomProperties for that single entity. Any values previously set via DYNSET are discarded in favor of the master block's authored defaults (block.dynamicMetadata.parameters[*].defaultValue). Useful when the user has experimented with DYNSET and wants to return to a known baseline. No geometry re-evaluation runs — storage-only operation.",
      examples: ["DYNRESET", "DR"],
      seeAlso: ["DYNLIST", "DYNSET"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.dynreset.label",
    labelKeyEn: "command.dynreset.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    { commandTypeLiteral: "dynamic/reset", file: REDUCER_GEOMETRY },
  ],
};

// ╔══ Block authoring palette wrappers ════════════════════════════════════╗

const BPARAMETER: CommandDescriptor = {
  id: "BPARAMETER",
  category: "dynamic",
  status: "implemented",
  // Block-editor authoring command — reached only as an internal step of the
  // AI CREATE_DYNAMIC_BLOCK action (which targets BLOCK), never as a direct
  // AI target. Not AI-callable (cf. ТЗ §10.1 BEDIT = no).
  aiCallable: false,
  aliases: { en: ["BPARAMETER", "BPARAM"], ru: ["БПАРАМЕТР"] },
  commandTypeLiterals: [
    "geometry/add-dynamic-parameter",
    "geometry/update-dynamic-parameter",
    "geometry/remove-dynamic-parameter",
  ],
  parameters: [
    {
      name: "blockId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор мастер-блока (BlockDefinition), к которому относится параметр. Резолвится из активной BEDIT-сессии.",
        en: "Identifier of the master BlockDefinition the parameter belongs to. Resolved from the active BEDIT session.",
      },
    },
    {
      name: "kind",
      type: "enum",
      enumValues: ["linear", "flip", "visibility", "basepoint"],
      required: false,
      description: {
        ru: "Вид параметра: linear / flip / visibility / basepoint. Палитра подсветит соответствующую вкладку.",
        en: "Parameter kind: linear / flip / visibility / basepoint. The palette highlights the matching tab.",
      },
    },
    {
      name: "parameterId",
      type: "string",
      required: false,
      description: {
        ru: "Идентификатор существующего параметра для update/remove операций. Не требуется для add (генерируется автоматически).",
        en: "Identifier of an existing parameter for update/remove operations. Not required for add (auto-generated).",
      },
    },
    {
      name: "name",
      type: "string",
      required: false,
      description: {
        ru: "Отображаемое имя параметра (по умолчанию — генерируется из kind, например 'Distance1', 'Visibility1').",
        en: "Display name of the parameter (defaults to a kind-derived name, e.g. 'Distance1', 'Visibility1').",
      },
    },
    {
      name: "defaultValue",
      type: "string",
      required: false,
      description: {
        ru: "Значение параметра по умолчанию: число для linear, имя состояния для visibility, true/false для flip.",
        en: "Default parameter value: number for linear, state name for visibility, true/false for flip.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Создание / редактирование / удаление параметра динамического блока (требует BEDIT-сессию).",
      long: "Команда БПАРАМЕТР (BPARAMETER) внутри сессии BEDIT (block-edit) открывает палитру авторинга блоков и переключает её на вкладку Parameter. Через палитру пользователь может: создать новый параметр (geometry/add-dynamic-parameter), отредактировать существующий — имя, диапазон, default value, choices (geometry/update-dynamic-parameter), удалить параметр и все привязанные действия (geometry/remove-dynamic-parameter). Поддерживаемые типы: linear (с диапазоном min/max и шагом), flip (зеркало по оси), visibility (переключение наборов сущностей), basepoint (опорная точка вставки). Без активной BEDIT-сессии — no-op + предупреждение.",
      examples: ["BPARAMETER", "БПАРАМЕТР", "BPARAM"],
      seeAlso: ["BACTION", "BVSTATE", "BCPARAMETER", "BLOOKUPTABLE"],
    },
    en: {
      short:
        "Create / edit / remove a dynamic block parameter (requires BEDIT session).",
      long: "The BPARAMETER command inside an active BEDIT session opens the Block Authoring Palette and switches it to the Parameter tab. Through the palette the user can: add a new parameter (geometry/add-dynamic-parameter), edit an existing one — name, range, default value, choices (geometry/update-dynamic-parameter), or remove a parameter and all attached actions (geometry/remove-dynamic-parameter). Supported kinds: linear (with min/max + step), flip (mirror across an axis), visibility (toggle entity sets), basepoint (insertion-point override). Outside BEDIT — no-op + warning.",
      examples: ["BPARAMETER", "BPARAM"],
      seeAlso: ["BACTION", "BVSTATE", "BCPARAMETER", "BLOOKUPTABLE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.bparameter.label",
    labelKeyEn: "command.bparameter.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    {
      commandTypeLiteral: "geometry/add-dynamic-parameter",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/update-dynamic-parameter",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/remove-dynamic-parameter",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
  ],
};

const BACTION: CommandDescriptor = {
  id: "BACTION",
  category: "dynamic",
  status: "implemented",
  // Block-editor authoring command (action authoring) — not a direct AI
  // target. Not AI-callable (cf. ТЗ §10.1 BEDIT = no).
  aiCallable: false,
  aliases: { en: ["BACTION", "BACT"], ru: ["БДЕЙСТВИЕ"] },
  // BACTION owns both the per-action CRUD literals and the chain-group
  // CRUD literals — chain groups are coordination of actions and belong
  // semantically to the Action tab. The palette uses chain-group editing
  // to define multi-step action sequences executed atomically when the
  // parameter value changes.
  commandTypeLiterals: [
    "geometry/add-dynamic-action",
    "geometry/update-dynamic-action",
    "geometry/remove-dynamic-action",
    "geometry/create-chain-group",
    "geometry/add-action-to-chain-group",
    "geometry/remove-action-from-chain",
    "geometry/reorder-chain-group",
  ],
  parameters: [
    {
      name: "blockId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор мастер-блока (BlockDefinition), внутри которого создаётся действие. Резолвится из активной BEDIT-сессии.",
        en: "Identifier of the master BlockDefinition the action belongs to. Resolved from the active BEDIT session.",
      },
    },
    {
      name: "parameterId",
      type: "string",
      required: false,
      description: {
        ru: "Идентификатор параметра, к которому привязывается действие. Если не указан — палитра предлагает выбрать в интерактивном режиме.",
        en: "Identifier of the parameter the action attaches to. When omitted, the palette prompts interactively.",
      },
    },
    {
      name: "actionId",
      type: "string",
      required: false,
      description: {
        ru: "Идентификатор существующего действия для update/remove операций. Не требуется для add (генерируется автоматически).",
        en: "Identifier of an existing action for update/remove operations. Not required for add (auto-generated).",
      },
    },
    {
      name: "kind",
      type: "enum",
      enumValues: ["move", "scale", "stretch", "rotate", "flip", "array"],
      required: false,
      description: {
        ru: "Вид действия: move / scale / stretch / rotate / flip / array. Доступные виды зависят от типа выбранного параметра.",
        en: "Action kind: move / scale / stretch / rotate / flip / array. Available kinds depend on the selected parameter's type.",
      },
    },
    {
      name: "selectionSet",
      type: "string",
      required: false,
      description: {
        ru: "Список идентификаторов сущностей (через запятую), на которые воздействует действие. Только для CRUD действий.",
        en: "Comma-separated list of entity identifiers the action affects. Only for action CRUD literals.",
      },
    },
    {
      name: "chainGroupId",
      type: "string",
      required: false,
      description: {
        ru: "Идентификатор chain-group для добавления/удаления/переупорядочивания членов цепочки.",
        en: "Chain-group identifier for adding / removing / reordering chain members.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Создание / редактирование действий и chain-group цепочек (требует BEDIT и параметр).",
      long: "Команда БДЕЙСТВИЕ (BACTION) внутри сессии BEDIT открывает палитру авторинга блоков на вкладке Action. Действие связывает существующий параметр (созданный через BPARAMETER) с конкретным мутирующим эффектом: move перемещает выбранный набор сущностей при изменении значения параметра, scale масштабирует, stretch растягивает (с указанием рамки), rotate вращает вокруг точки, flip зеркалирует, array реплицирует по сетке. Поддерживаются: создание (geometry/add-dynamic-action), редактирование рамки/набора/типа (geometry/update-dynamic-action), удаление (geometry/remove-dynamic-action). Дополнительно команда покрывает chain-group цепочки — последовательности действий, исполняемые атомарно при изменении параметра: создание группы (geometry/create-chain-group), включение/исключение действия из цепочки (geometry/add-action-to-chain-group / geometry/remove-action-from-chain), переупорядочивание членов (geometry/reorder-chain-group). Без активной BEDIT-сессии — no-op + предупреждение.",
      examples: ["BACTION", "БДЕЙСТВИЕ", "BACT"],
      seeAlso: ["BPARAMETER", "BVSTATE"],
    },
    en: {
      short:
        "Create / edit actions and chain-group sequences (requires BEDIT + a parameter).",
      long: "The BACTION command inside a BEDIT session opens the Block Authoring Palette on the Action tab. An action ties an existing parameter (created via BPARAMETER) to a concrete mutating effect: move translates the selected entity set when the parameter value changes, scale scales them, stretch stretches them (with a window pick), rotate rotates around a point, flip mirrors, array replicates on a grid. Supported literals: create (geometry/add-dynamic-action), edit the window / selection set / kind (geometry/update-dynamic-action), remove (geometry/remove-dynamic-action). The descriptor additionally owns chain-group editing — sequences of actions that execute atomically when the parameter changes: create group (geometry/create-chain-group), include / exclude an action (geometry/add-action-to-chain-group / geometry/remove-action-from-chain), reorder members (geometry/reorder-chain-group). Outside BEDIT — no-op + warning.",
      examples: ["BACTION", "BACT"],
      seeAlso: ["BPARAMETER", "BVSTATE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.baction.label",
    labelKeyEn: "command.baction.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    {
      commandTypeLiteral: "geometry/add-dynamic-action",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/update-dynamic-action",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/remove-dynamic-action",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/create-chain-group",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/add-action-to-chain-group",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/remove-action-from-chain",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/reorder-chain-group",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
  ],
};

const BAUTHORPALETTE: CommandDescriptor = {
  id: "BAUTHORPALETTE",
  category: "dynamic",
  status: "stub",
  // Block-editor authoring palette (UI) — not AI-callable.
  aiCallable: false,
  aliases: { en: ["BAUTHORPALETTE"], ru: ["БПАЛИТРА", "АПАЛИТРА"] },
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short:
        "Переключает видимость палитры авторинга блоков (внутри BEDIT-сессии).",
      long: "Команда БПАЛИТРА (BAUTHORPALETTE) переключает видимость Block Authoring Palette в активной BEDIT-сессии. Палитра сама монтируется только когда активное пространство — block-edit; флаг лишь скрывает / показывает её внутри этой сессии. Вне BEDIT команда — silent no-op (AutoCAD ведёт себя так же). Парная команда BAUTHORPALETTECLOSE гарантированно закрывает палитру, тогда как BAUTHORPALETTE — toggle.",
      examples: ["BAUTHORPALETTE", "БПАЛИТРА"],
      seeAlso: ["BPARAMETER", "BACTION", "BVSTATE"],
    },
    en: {
      short:
        "Toggles visibility of the Block Authoring Palette inside an active BEDIT session.",
      long: "The BAUTHORPALETTE command toggles the visibility of the Block Authoring Palette inside the active BEDIT session. The palette itself only mounts when the active space is block-edit; the flag merely hides / shows it within that session. Outside BEDIT the command is a silent no-op (AutoCAD behaves the same). The companion BAUTHORPALETTECLOSE always closes the palette, whereas BAUTHORPALETTE is a toggle.",
      examples: ["BAUTHORPALETTE"],
      seeAlso: ["BPARAMETER", "BACTION", "BVSTATE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.bauthorpalette.label",
    labelKeyEn: "command.bauthorpalette.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

const BVSTATE: CommandDescriptor = {
  id: "BVSTATE",
  category: "dynamic",
  status: "implemented",
  // Block-editor visibility-state AUTHORING command — not a direct AI target
  // (instance-level visibility changes go through DYNSET via the AI
  // SET_VISIBILITY_STATE action). Not AI-callable.
  aiCallable: false,
  aliases: { en: ["BVSTATE", "BVS"], ru: ["БСОСТВИД", "БСОСТ"] },
  commandTypeLiterals: [
    "geometry/add-visibility-state",
    "geometry/rename-visibility-state",
    "geometry/delete-visibility-state",
    "geometry/reorder-visibility-states",
    "geometry/set-default-visibility-state",
    "geometry/toggle-visibility-state-entity",
  ],
  parameters: [
    {
      name: "blockId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор мастер-блока (BlockDefinition), которому принадлежат состояния видимости. Резолвится из активной BEDIT-сессии.",
        en: "Identifier of the master BlockDefinition that owns the visibility states. Resolved from the active BEDIT session.",
      },
    },
    {
      name: "parameterId",
      type: "string",
      required: true,
      description: {
        ru: "Идентификатор visibility-параметра (созданного через BPARAMETER), к которому привязаны состояния.",
        en: "Identifier of the visibility parameter (created via BPARAMETER) the states attach to.",
      },
    },
    {
      name: "stateName",
      type: "string",
      required: false,
      description: {
        ru: "Имя состояния видимости (для add/rename/delete/set-default). Должно быть уникальным в рамках одного параметра.",
        en: "Visibility state name (for add/rename/delete/set-default). Must be unique within a parameter.",
      },
    },
    {
      name: "newStateName",
      type: "string",
      required: false,
      description: {
        ru: "Новое имя состояния (только для rename-visibility-state).",
        en: "New state name (rename-visibility-state only).",
      },
    },
    {
      name: "stateOrder",
      type: "string",
      required: false,
      description: {
        ru: "Новый порядок состояний — список имён через запятую (только для reorder-visibility-states).",
        en: "New state order — comma-separated list of names (reorder-visibility-states only).",
      },
    },
    {
      name: "entityId",
      type: "entityId",
      required: false,
      description: {
        ru: "Идентификатор сущности внутри блока для toggle (включения / исключения её скрытия в состоянии).",
        en: "Entity identifier inside the block to toggle (include / exclude from being hidden in the state).",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Создание / редактирование состояний видимости параметра visibility (требует BEDIT).",
      long: "Команда БСОСТВИД (BVSTATE) внутри сессии BEDIT открывает палитру авторинга блоков на вкладке Visibility. Эта вкладка позволяет создавать (geometry/add-visibility-state), переименовывать (geometry/rename-visibility-state), удалять (geometry/delete-visibility-state) и переупорядочивать (geometry/reorder-visibility-states) состояния видимости для параметра visibility, назначать состояние по умолчанию (geometry/set-default-visibility-state), а также включать / исключать конкретные сущности из набора скрываемых в данном состоянии (geometry/toggle-visibility-state-entity). Без активной BEDIT-сессии — no-op + предупреждение.",
      examples: ["BVSTATE", "БСОСТВИД", "BVS"],
      seeAlso: ["BPARAMETER", "BACTION"],
    },
    en: {
      short:
        "Create / edit visibility states for a visibility parameter (requires BEDIT).",
      long: "The BVSTATE command inside a BEDIT session opens the Block Authoring Palette on the Visibility tab. This tab lets the user create (geometry/add-visibility-state), rename (geometry/rename-visibility-state), delete (geometry/delete-visibility-state) and reorder (geometry/reorder-visibility-states) visibility states for a visibility parameter, designate a default state (geometry/set-default-visibility-state), and include / exclude specific entities from the hidden set per state (geometry/toggle-visibility-state-entity). Outside BEDIT — no-op + warning.",
      examples: ["BVSTATE", "BVS"],
      seeAlso: ["BPARAMETER", "BACTION"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.bvstate.label",
    labelKeyEn: "command.bvstate.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    {
      commandTypeLiteral: "geometry/add-visibility-state",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/rename-visibility-state",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/delete-visibility-state",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/reorder-visibility-states",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/set-default-visibility-state",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/toggle-visibility-state-entity",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
  ],
};

const BCPARAMETER: CommandDescriptor = {
  id: "BCPARAMETER",
  category: "dynamic",
  status: "stub",
  // Block-editor constraint-parameter authoring — not AI-callable.
  aiCallable: false,
  aliases: { en: ["BCPARAMETER", "BCPARAM"], ru: ["БСВЯЗПАР", "БКПАРАМ"] },
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short:
        "Открывает палитру авторинга на вкладке геометрических ограничений (требует BEDIT).",
      long: "Команда БСВЯЗПАР (BCPARAMETER) внутри сессии BEDIT открывает палитру авторинга и переключает её на вкладку Constraints. В AutoCAD BCPARAMETER — это мастер преобразования размерного ограничения в параметр; в нашей Tier 2 реализации параметры-ограничения создаются напрямую через UI палитры, поэтому команда сводится к переключению вкладки. Без активной BEDIT-сессии — no-op + предупреждение. Фактическая привязка геометрических ограничений к параметру блока выполняется через geometry/add-geometric-constraint после интерактивного выбора пары сущностей.",
      examples: ["BCPARAMETER", "БСВЯЗПАР"],
      seeAlso: ["BPARAMETER", "GCCONSTRAINT"],
    },
    en: {
      short:
        "Opens the authoring palette on the Constraints tab (requires BEDIT).",
      long: "The BCPARAMETER command inside a BEDIT session opens the Block Authoring Palette and switches it to the Constraints tab. AutoCAD's BCPARAMETER is closer to a 'convert dimensional constraint to parameter' wizard, but in this Tier 2 implementation constraint parameters are created directly through the palette UI, so the command is reduced to a tab-switch trigger. Outside BEDIT — no-op + warning. Actual binding of geometric constraints to a block parameter happens via geometry/add-geometric-constraint after an interactive entity-pair pick.",
      examples: ["BCPARAMETER", "BCPARAM"],
      seeAlso: ["BPARAMETER", "GCCONSTRAINT"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.bcparameter.label",
    labelKeyEn: "command.bcparameter.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

const BLOOKUPTABLE: CommandDescriptor = {
  id: "BLOOKUPTABLE",
  category: "dynamic",
  status: "implemented",
  // Block-editor lookup-table AUTHORING command — not a direct AI target
  // (instance-level lookup selection goes through DYNSET via the AI
  // LOOKUP_SELECT action). Not AI-callable.
  aiCallable: false,
  aliases: { en: ["BLOOKUPTABLE"], ru: ["БТАБЛПРОСМ", "БТАБЛВЫБОРКА", "БВЫБ"] },
  commandTypeLiterals: [
    "geometry/create-lookup-table",
    "geometry/update-lookup-table",
    "geometry/delete-lookup-table",
  ],
  parameters: [
    {
      name: "tableId",
      type: "string",
      required: false,
      description: {
        ru: "Идентификатор lookup-таблицы для редактирования. Если не указан и таблиц несколько — открывается первая; если ни одной — создаётся пустая.",
        en: "Lookup table id to edit. When omitted and multiple exist the first opens; when none exist an empty table is minted and opened.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Открывает редактор Lookup Table для активного блока (создаёт пустую таблицу при отсутствии).",
      long: "Команда БТАБЛПРОСМ (BLOOKUPTABLE) внутри сессии BEDIT открывает диалог редактирования Lookup Table для активного блока. Если в блоке уже есть одна или несколько lookup-таблиц — открывается первая; если ни одной — система автоматически минтит пустую таблицу (с уникальным id вида lookup_table-N и именем TableN), диспатчит geometry/create-lookup-table и открывает её в диалоге. Lookup Table связывает входные значения параметров с выходными — например, выбор размера двери из списка автоматически устанавливает width/height. Без BEDIT — no-op + предупреждение. Сохранение изменений диспатчит geometry/update-lookup-table.",
      examples: ["BLOOKUPTABLE", "БТАБЛПРОСМ", "БВЫБ"],
      seeAlso: ["BTABLE", "BPARAMETER"],
    },
    en: {
      short:
        "Opens the Lookup Table editor for the active block (creates an empty table if none exists).",
      long: "The BLOOKUPTABLE command inside a BEDIT session opens the Lookup Table editor dialog for the active block. If the block already has one or more lookup tables the first opens; if none exist the system mints an empty one (with a unique id of the form lookup_table-N and name TableN), dispatches geometry/create-lookup-table and opens it in the dialog. A Lookup Table maps input parameter values to outputs — for example selecting a door size from a list automatically sets width / height. Outside BEDIT — no-op + warning. Saving changes dispatches geometry/update-lookup-table.",
      examples: ["BLOOKUPTABLE"],
      seeAlso: ["BTABLE", "BPARAMETER"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.blookuptable.label",
    labelKeyEn: "command.blookuptable.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    {
      commandTypeLiteral: "geometry/create-lookup-table",
      file: REDUCER_DYNAMIC_LOOKUP,
    },
    {
      commandTypeLiteral: "geometry/update-lookup-table",
      file: REDUCER_DYNAMIC_LOOKUP,
    },
    {
      commandTypeLiteral: "geometry/delete-lookup-table",
      file: REDUCER_DYNAMIC_LOOKUP,
    },
  ],
};

const BTABLE: CommandDescriptor = {
  id: "BTABLE",
  category: "dynamic",
  status: "implemented",
  // Block-editor properties-table AUTHORING command — the AI authors dynamic
  // blocks via CREATE_DYNAMIC_BLOCK (targets BLOCK), not by invoking BTABLE
  // directly. Not AI-callable.
  aiCallable: false,
  aliases: { en: ["BTABLE"], ru: ["БТАБЛИЦА", "БТАБЛСВОЙСТВА", "БПТ"] },
  commandTypeLiterals: [
    "geometry/set-block-properties-table",
    "geometry/add-block-property",
    "geometry/update-block-property",
    "geometry/remove-block-property",
    "geometry/reorder-block-properties",
  ],
  parameters: [],
  help: {
    ru: {
      short:
        "Открывает редактор Block Properties Table (BPT) для активного блока.",
      long: "Команда БТАБЛИЦА (BTABLE) внутри сессии BEDIT открывает редактор Block Properties Table — таблицы свойств блока, в которой каждая строка задаёт набор значений параметров для одного 'варианта' блока (типового конструктива). Пользователь может создавать, обновлять, удалять и переупорядочивать строки. Сохранение изменений диспатчит соответствующий literal: geometry/set-block-properties-table (полная замена), geometry/add-block-property, geometry/update-block-property, geometry/remove-block-property, geometry/reorder-block-properties. Без BEDIT — no-op + предупреждение.",
      examples: ["BTABLE", "БТАБЛИЦА", "БПТ"],
      seeAlso: ["BLOOKUPTABLE", "BPARAMETER"],
    },
    en: {
      short:
        "Opens the Block Properties Table (BPT) editor for the active block.",
      long: "The BTABLE command inside a BEDIT session opens the Block Properties Table editor — a table where each row defines a parameter-value set for one 'variant' of the block (a configuration). The user can create, update, delete and reorder rows. Saving dispatches the matching literal: geometry/set-block-properties-table (full replacement), geometry/add-block-property, geometry/update-block-property, geometry/remove-block-property, geometry/reorder-block-properties. Outside BEDIT — no-op + warning.",
      examples: ["BTABLE"],
      seeAlso: ["BLOOKUPTABLE", "BPARAMETER"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.btable.label",
    labelKeyEn: "command.btable.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [
    {
      commandTypeLiteral: "geometry/set-block-properties-table",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/add-block-property",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/update-block-property",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/remove-block-property",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
    {
      commandTypeLiteral: "geometry/reorder-block-properties",
      file: REDUCER_DYNAMIC_AUTHORING,
    },
  ],
};

const BGRIPSET: CommandDescriptor = {
  id: "BGRIPSET",
  category: "dynamic",
  status: "stub",
  // Block-editor grip authoring — not AI-callable.
  aiCallable: false,
  aliases: { en: ["BGRIPSET"], ru: ["БРУЧКА", "БГРИПНАБОР"] },
  commandTypeLiterals: [],
  parameters: [],
  help: {
    ru: {
      short:
        "Заглушка: команда отображения / переключения ручек (grip) параметров блока.",
      long: "Команда БРУЧКА (BGRIPSET) в AutoCAD управляет видимостью и положением ручек (grips) параметров динамического блока — ручки появляются на INSERT'е и позволяют пользователю интерактивно изменять значения параметров без открытия диалога. В текущей Tier 1 реализации команда принимает ввод и пишет в командную строку информационное сообщение, но полноценный диалог управления ручками появится в более поздней фазе (отслеживается в DYN-1.11 плане). Без BEDIT-сессии — no-op + предупреждение.",
      examples: ["BGRIPSET", "БРУЧКА"],
      seeAlso: ["BPARAMETER", "BACTION"],
    },
    en: {
      short:
        "Stub: block parameter grip-display / grip-set toggle. Full UI scheduled for a later tier.",
      long: "AutoCAD's BGRIPSET command controls the visibility and placement of grips on a dynamic block parameter — grips appear on the INSERT and let the user interactively change parameter values without opening a dialog. In the current Tier 1 implementation the command accepts the typed name and writes an informational message to the command line, but the full grip-management dialog will land in a later tier (tracked in the DYN-1.11 plan). Outside BEDIT — no-op + warning.",
      examples: ["BGRIPSET"],
      seeAlso: ["BPARAMETER", "BACTION"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.bgripset.label",
    labelKeyEn: "command.bgripset.label",
    ribbonTab: null,
    ribbonSection: null,
    glyph: null,
  },
  reducerLocations: [],
};

export const DYNAMIC_DESCRIPTORS: readonly CommandDescriptor[] = [
  BACTION,
  BAUTHORPALETTE,
  BCPARAMETER,
  BGRIPSET,
  BLOOKUPTABLE,
  BPARAMETER,
  BTABLE,
  BVSTATE,
  DYNLIST,
  DYNRESET,
  DYNSET,
];
